import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as api from './api.ts';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  Metric,
  Metrics,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  type Tone,
} from './ui.tsx';

/** A message to show the admin, carrying its outcome so a failure never reads as a success. */
type Note = { text: string; tone: Tone };

/** How the admin is asked to say something — text plus the tone it reads in. */
type Notify = (text: string, tone?: Tone) => void;

/**
 * The four roles an admin may assign, in the same order and with the same
 * wording as the invite control, so the role a person is given reads the same
 * whether it is set at invite time or changed later. `owner` is deliberately
 * absent — the seat moves through the transfer flow on the People screen.
 */
// Plain names, no capability blurbs: "Staff — sweeps" claimed a duty the role
// does not carry (sweeping is a warden assignment on the ECO screen, a
// different axis from role — docs/teams.md), and a wrong claim in a select is
// read as the product's word (owner correction 2026-09-21).
const ASSIGNABLE_ROLES: { value: api.RosterRole | 'viewer'; label: string }[] = [
  { value: 'staff', label: 'Staff' },
  { value: 'occupant', label: 'Occupant' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'admin', label: 'Administrator' },
];

type AssignableRole = 'admin' | 'viewer' | 'staff' | 'occupant';

/**
 * Turn a mutation error into a note that reads as a failure — an alert tone,
 * never the neutral one a success wears. A freshness 403 (an admin mutation
 * whose session is no longer fresh enough) is translated into a sentence that
 * names the fix rather than showing a raw refusal. No inline re-verify action
 * is available in this shared component, so the copy is all it can offer.
 */
function reportError(err: unknown, notify: Notify) {
  if (err instanceof api.ApiError && err.status === 403) {
    notify("For safety, confirm it's you — sign in again to continue", 'alert');
    return;
  }
  notify(api.errorMessage(err) ?? 'That could not be done.', 'alert');
}

/**
 * Roster management — the roster table, one-person invite, and the CSV reconcile
 * — in one component. Named `RosterManager` (not `TeamManager`) because a Team is
 * now a distinct entity (`docs/teams.md`); this is about *who is on the roster*,
 * which is a different axis.
 *
 * It is the surface the **setup wizard's** team step uses to fill a building's
 * roster for the first time. On the ongoing **People** screen the roster table it
 * carries is absorbed into the one canonical People directory (a person appears
 * once, with inline controls), so People uses the `Invite` and `LoadARoster`
 * pieces directly from the page's top nav rather than mounting the whole manager.
 *
 * What it does not hold: ownership transfer and guardian follows. Those are
 * decisions about a single person's standing, not roster maintenance, and they
 * live on the People screen beside this rather than inside it.
 */
export function RosterManager({
  orgId,
  intro,
  showMetrics = true,
}: {
  orgId: string;
  /** A sentence for the context this is embedded in (the wizard says something the People screen does not). */
  intro?: ReactNode;
  showMetrics?: boolean;
}) {
  const qc = useQueryClient();
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const [note, setNote] = useState<Note | null>(null);
  const notify: Notify = (text, tone = 'neutral') => setNote({ text, tone });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['roster', orgId] });

  if (roster.isLoading || !roster.data) return <Spinner />;

  const { members, pendingInvitations, counts } = roster.data;

  return (
    <>
      {intro ? (
        <div className="mb-6 max-w-[640px] text-[14px] text-[var(--ink-2)]">{intro}</div>
      ) : null}

      {showMetrics ? (
        <Metrics>
          <Metric label="Active" value={counts.active} />
          <Metric label="Administrators" value={counts.admins} />
          <Metric
            label="Awaiting reply"
            value={counts.pending}
            tone={counts.pending > 0 ? 'attention' : 'neutral'}
          />
          <Metric label="Removed" value={counts.deprovisioned} />
        </Metrics>
      ) : null}

      {note ? (
        <div className="mt-6">
          <Finding tone={note.tone}>{note.text}</Finding>
        </div>
      ) : null}

      <div className="mt-8">
        <Roster
          orgId={orgId}
          members={members}
          pendingInvitations={pendingInvitations}
          onNote={notify}
          onChanged={refresh}
        />
      </div>

      <Invite orgId={orgId} onNote={notify} onInvited={refresh} />

      <LoadARoster orgId={orgId} />
    </>
  );
}

/** Human label for a role, without collapsing five roles into two words. */
export const roleLabel = (r: api.Role): string =>
  r === 'owner'
    ? 'Owner'
    : r === 'admin'
      ? 'Administrator'
      : r === 'viewer'
        ? 'Viewer'
        : r === 'staff'
          ? 'Staff'
          : 'Occupant';

/**
 * The roster itself: active members and pending invitations in one table,
 * because "invited three weeks ago and never accepted" is a gap in coverage,
 * not an administrative footnote on another tab.
 *
 * Pending rows are marked `Not yet accepted` (or `Invitation expired`) and
 * carry Resend and Remove; active rows carry a role toggle and Remove. Owner is
 * never editable here — the seat moves through the transfer flow on the People
 * screen — so it reads as a plain label.
 */
function Roster({
  orgId,
  members,
  pendingInvitations,
  onNote,
  onChanged,
}: {
  orgId: string;
  members: api.Person[];
  pendingInvitations: api.PendingInvitation[];
  onNote: Notify;
  onChanged: () => void;
}) {
  const [query, setQuery] = useState('');
  // Structured filters, the way a large roster is made navigable — not an open
  // search over people who might not be on it. Free text still narrows the
  // org's OWN roster (the rows already loaded here), but role / reach / status
  // are what carry a building of hundreds, and they compose.
  const [roleFilter, setRoleFilter] = useState<'all' | api.Role>('all');
  const [reachFilter, setReachFilter] = useState<'all' | 'app' | 'reachable' | 'none'>('all');
  const [statusFilter, setStatusFilter] = useState<
    'current' | 'active' | 'pending' | 'deprovisioned'
  >('current');
  // Client-side paging: the roster arrives whole (one org, one response), so a
  // building of hundreds is a scroll problem, not a fetch one. A page size keeps
  // the table scannable; the controls below say where in the list you are.
  const [page, setPage] = useState(0);

  const remove = useMutation({
    // The same control removes an active member and withdraws a pending
    // invitation, and the two owe different words: a person who is on the roster
    // "will no longer be alerted"; an invitation was simply withdrawn.
    mutationFn: ({ id }: { id: string; pending: boolean }) => api.removePerson(orgId, id),
    onSuccess: (res, { pending }) => {
      if (pending) {
        onNote('Invitation withdrawn.');
      } else if (res?.vacatedEcoRoles?.length) {
        // The backend reports any emergency-control roles this person held that
        // are now vacant. Name them on the spot rather than leave them to be
        // discovered on a readiness screen a fortnight later.
        onNote(`Removed. This also vacated: ${res.vacatedEcoRoles.join(', ')}.`);
      } else {
        onNote('Removed. They will no longer be alerted.');
      }
      onChanged();
    },
    // The server refuses to remove the last owner/admin; surface that as a
    // failure, not a neutral note that reads like it worked.
    onError: (e) => reportError(e, onNote),
  });

  const resend = useMutation({
    mutationFn: (invitationId: string) => api.resendInvitation(orgId, invitationId),
    onSuccess: () => {
      onNote('A fresh invitation is on its way. The earlier link no longer works.');
      onChanged();
    },
    onError: (e) => reportError(e, onNote),
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: AssignableRole }) =>
      api.setRole(orgId, userId, role),
    onSuccess: onChanged,
    onError: (e) => reportError(e, onNote),
  });

  const q = query.trim().toLowerCase();
  const matches = (name: string | null, email: string | null) =>
    !q || (name ?? '').toLowerCase().includes(q) || (email ?? '').toLowerCase().includes(q);

  const reachOf = (m: api.Person) => {
    if (reachFilter === 'all') return true;
    if (reachFilter === 'app') return m.reach.app;
    if (reachFilter === 'reachable') return m.reach.app || m.reach.sms || m.reach.email;
    return !m.reach.app && !m.reach.sms && !m.reach.email; // 'none'
  };
  const roleOf = (r: api.Role) => roleFilter === 'all' || r === roleFilter;

  // Whether each population is in scope for the current status filter. Reach and
  // role are person properties, so a pending invitation (no channels yet, only
  // an intended role) is only ever shown when reach is unconstrained.
  const wantMembers = statusFilter !== 'pending';
  const wantPending = statusFilter === 'current' || statusFilter === 'pending';
  const wantActive = statusFilter === 'current' || statusFilter === 'active';
  const wantDeprovisioned = statusFilter === 'deprovisioned';

  const memberRows = members.filter((m) => {
    const isActive = !m.deprovisioned_at;
    if (!wantMembers) return false;
    if (isActive ? !wantActive : !wantDeprovisioned) return false;
    return matches(m.display_name, m.email) && roleOf(m.role) && reachOf(m);
  });
  const pendingRows =
    wantPending && reachFilter === 'all'
      ? pendingInvitations.filter((i) => matches(null, i.email) && roleOf(i.role as api.Role))
      : [];

  // One combined, paged list — members first, then pending — so the page window
  // is over everything the filters admit rather than two independently-paged
  // tables.
  type RosterRow =
    { kind: 'member'; person: api.Person } | { kind: 'pending'; invitation: api.PendingInvitation };
  const allRows: RosterRow[] = [
    ...memberRows.map((person) => ({ kind: 'member' as const, person })),
    ...pendingRows.map((invitation) => ({ kind: 'pending' as const, invitation })),
  ];

  const PAGE_SIZE = 25;
  const total = allRows.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Clamp: a filter change can shrink the list under the current page.
  const current = Math.min(page, pageCount - 1);
  const start = current * PAGE_SIZE;
  const pageRows = allRows.slice(start, start + PAGE_SIZE);

  const filtering =
    Boolean(q) || roleFilter !== 'all' || reachFilter !== 'all' || statusFilter !== 'current';

  // Any filter change returns to the first page — otherwise a narrow filter can
  // land on an empty page 4 of a 1-page result.
  const setFilter =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(0);
    };

  return (
    <Section title="People">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-[280px]">
          <Field
            label="Find someone"
            value={query}
            onChange={setFilter(setQuery)}
            placeholder="Name or email"
          />
        </div>
        <div className="w-[180px]">
          <Select
            label="Access"
            value={roleFilter}
            onChange={setFilter((v: string) => setRoleFilter(v as 'all' | api.Role))}
            options={[
              { value: 'all', label: 'Any access' },
              { value: 'owner', label: 'Owner' },
              { value: 'admin', label: 'Administrator' },
              { value: 'viewer', label: 'Viewer' },
              { value: 'staff', label: 'Staff' },
              { value: 'occupant', label: 'Occupant' },
            ]}
          />
        </div>
        <div className="w-[190px]">
          <Select
            label="Reachable by"
            value={reachFilter}
            onChange={setFilter((v: string) =>
              setReachFilter(v as 'all' | 'app' | 'reachable' | 'none'),
            )}
            options={[
              { value: 'all', label: 'Any channel' },
              { value: 'app', label: 'Has the app' },
              { value: 'reachable', label: 'Any channel at all' },
              { value: 'none', label: 'On no channel' },
            ]}
          />
        </div>
        <div className="w-[190px]">
          <Select
            label="Status"
            value={statusFilter}
            onChange={setFilter((v: string) =>
              setStatusFilter(v as 'current' | 'active' | 'pending' | 'deprovisioned'),
            )}
            options={[
              { value: 'current', label: 'Active & invited' },
              { value: 'active', label: 'Active' },
              { value: 'pending', label: 'Invited, not accepted' },
              { value: 'deprovisioned', label: 'Removed' },
            ]}
          />
        </div>
      </div>

      {total === 0 ? (
        filtering ? (
          <Empty>Nobody matches these filters.</Empty>
        ) : (
          <Empty>Nobody here yet. Invite the first person below.</Empty>
        )
      ) : (
        <>
          <Table head={['Name', 'Email', 'Access', 'Reach', 'Status', '']}>
            {pageRows.map((row) =>
              row.kind === 'member' ? (
                <MemberRow
                  key={row.person.user_id}
                  m={row.person}
                  changePending={changeRole.isPending}
                  onChangeRole={(role) => changeRole.mutate({ userId: row.person.user_id, role })}
                  onRemove={() => remove.mutate({ id: row.person.user_id, pending: false })}
                />
              ) : (
                <Row key={row.invitation.id}>
                  <Cell>—</Cell>
                  <Cell wide>{row.invitation.email}</Cell>
                  <Cell>{roleLabel(row.invitation.role as api.Role)}</Cell>
                  <Cell>
                    <span className="text-[var(--ink-2)]">—</span>
                  </Cell>
                  <Cell>
                    <Status tone="attention">
                      {row.invitation.expired ? 'Invited · expired' : 'Invited'}
                    </Status>
                  </Cell>
                  <Cell>
                    <span className="inline-flex items-center gap-2">
                      <Button
                        variant="quiet"
                        disabled={resend.isPending}
                        label={`Resend the invitation to ${row.invitation.email}`}
                        onClick={() => resend.mutate(row.invitation.id)}
                      >
                        Resend
                      </Button>
                      <Button
                        variant="quiet"
                        label={`Withdraw the invitation to ${row.invitation.email}`}
                        onClick={() => remove.mutate({ id: row.invitation.id, pending: true })}
                      >
                        Remove
                      </Button>
                    </span>
                  </Cell>
                </Row>
              ),
            )}
          </Table>

          {/* Where you are in the list, and the way through it. Only rendered
              when there is more than one page — a roster that fits on one needs
              no controls. */}
          {pageCount > 1 ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[13px] text-[var(--ink-2)]">
                Showing <span className="tabular">{start + 1}</span>–
                <span className="tabular">{start + pageRows.length}</span> of{' '}
                <span className="tabular">{total}</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  disabled={current === 0}
                  label="Previous page"
                  onClick={() => setPage(current - 1)}
                >
                  Previous
                </Button>
                <span className="text-[13px] text-[var(--ink-2)]">
                  Page <span className="tabular">{current + 1}</span> of{' '}
                  <span className="tabular">{pageCount}</span>
                </span>
                <Button
                  variant="secondary"
                  disabled={current >= pageCount - 1}
                  label="Next page"
                  onClick={() => setPage(current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

/**
 * One roster row for a member: the role select, the reach marks, and remove.
 * Pulled out so the paged list stays legible and the reach column reads the
 * same predicate the People screen's Reach section does — app, then SMS, then
 * email, each a word so it survives greyscale (design-dashboard.md §5).
 */
function MemberRow({
  m,
  changePending,
  onChangeRole,
  onRemove,
}: {
  m: api.Person;
  changePending: boolean;
  onChangeRole: (role: AssignableRole) => void;
  onRemove: () => void;
}) {
  return (
    <Row>
      <Cell>{m.display_name ?? '—'}</Cell>
      <Cell wide>{m.email ?? <span className="text-[var(--ink-2)]">no address</span>}</Cell>
      <Cell>{roleLabel(m.role)}</Cell>
      <Cell>
        <ReachCell reach={m.reach} />
      </Cell>
      <Cell>
        {m.deprovisioned_at ? (
          <Status tone="neutral">Removed</Status>
        ) : (
          <Status tone="safe">Active</Status>
        )}
      </Cell>
      <Cell>
        {m.role === 'owner' || m.deprovisioned_at ? (
          <span className="text-[var(--ink-2)]">{m.role === 'owner' ? 'Owner' : '—'}</span>
        ) : (
          <span className="inline-flex items-center gap-2">
            {/*
              A role select over the four assignable roles, not a binary
              Admin/Staff toggle: a toggle mislabels a Viewer or an Occupant as
              "Make admin", and could never move somebody to those roles at all.
            */}
            <select
              className="h-8 rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-2 text-[13px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
              value={m.role}
              disabled={changePending}
              aria-label={`Access for ${m.display_name ?? m.email ?? 'this person'}`}
              onChange={(e) => onChangeRole(e.target.value as AssignableRole)}
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <Button
              variant="quiet"
              label={`Remove ${m.display_name ?? m.email ?? 'this person'}`}
              onClick={onRemove}
            >
              Remove
            </Button>
          </span>
        )}
      </Cell>
    </Row>
  );
}

/** The three channels as words — never a lone dot (greyscale test). */
function ReachCell({ reach }: { reach: api.Person['reach'] }) {
  const none = !reach.app && !reach.sms && !reach.email;
  if (none) return <Status tone="alert">No channel</Status>;
  const on: string[] = [];
  if (reach.app) on.push('App');
  if (reach.sms) on.push('SMS');
  if (reach.email) on.push('Email');
  return <span className="text-[13px] text-[var(--ink-2)]">{on.join(', ')}</span>;
}

/** Invite one person by email, role — and the team they are joining. */
export function Invite({
  orgId,
  onNote,
  onInvited,
  bare,
}: {
  orgId: string;
  onNote: Notify;
  onInvited: () => void;
  /**
   * Render the form alone, without the "Invite somebody" section heading — for
   * hosts (the People inspect overlay) whose own title already says it, where
   * the heading read twice in a row.
   */
  bare?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableRole>('staff');
  const [teamId, setTeamId] = useState('');

  // Which team is this person joining? Asked at invite time (owner ask,
  // 2026-09-21) and applied when they accept. The control only renders when
  // the org has teams — an org with none is not asked an unanswerable question.
  const teams = useQuery({ queryKey: ['teams', orgId], queryFn: () => api.getTeams(orgId) });
  const teamOptions = [
    { value: '', label: 'No team' },
    ...(teams.data?.teams ?? []).map((t) => ({ value: t.id, label: t.name })),
  ];

  const send = useMutation({
    mutationFn: () => api.invitePerson(orgId, email, role, teamId || undefined),
    onSuccess: (res) => {
      setEmail('');
      setTeamId('');
      onNote(
        res?.status === 'already_a_member'
          ? 'That person is already on your list.'
          : 'Invitation sent. It can be declined in one tap.',
      );
      onInvited();
    },
    onError: (e) => reportError(e, onNote),
  });

  const form = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-[300px]">
        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          inputMode="email"
          placeholder="name@school.edu"
          hint="They can decline in one tap, without creating an account."
        />
      </div>
      <div className="w-[180px]">
        <Select
          label="Access"
          value={role}
          onChange={(v) => setRole(v as AssignableRole)}
          options={ASSIGNABLE_ROLES}
        />
      </div>
      {(teams.data?.teams.length ?? 0) > 0 ? (
        <div className="w-[200px]">
          <Select label="Team" value={teamId} onChange={setTeamId} options={teamOptions} />
        </div>
      ) : null}
      <Button variant="primary" disabled={!email || send.isPending} onClick={() => send.mutate()}>
        Send invitation
      </Button>
    </div>
  );

  return bare ? form : <Section title="Invite somebody">{form}</Section>;
}

/**
 * A whole building at once, reconciled rather than appended.
 *
 * The preview is the feature: reconciling takes people off, and the removals
 * have to be on screen before they can be applied. A plan that would take off
 * **more than half** the active roster is refused outright until an admin has
 * confirmed it — the guard that stops a truncated export from silently gutting
 * a building. The digest from the preview is presented back on apply, so a file
 * edited in between is refused rather than quietly removing somebody nobody saw
 * listed.
 */
export function LoadARoster({ orgId }: { orgId: string }) {
  const client = useQueryClient();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [plan, setPlan] = useState<api.RosterPlan | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // The >50%-removal confirmation gate: false until the admin is asked.
  const [confirmingLarge, setConfirmingLarge] = useState(false);

  // The destructive confirm is a real dialog (M5). Focus moves into it on open
  // and returns to the control that opened it on cancel, so a keyboard or
  // screen-reader user is never dropped somewhere with no context.
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const openLargeConfirm = () => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setConfirmingLarge(true);
  };
  const cancelLargeConfirm = () => {
    setConfirmingLarge(false);
    returnFocusRef.current?.focus();
  };

  useEffect(() => {
    if (confirmingLarge) dialogRef.current?.focus();
  }, [confirmingLarge]);

  function take(file: File | undefined) {
    if (!file) return;
    setProblem(null);
    setPlan(null);
    setDone(null);
    setConfirmingLarge(false);
    setFileName(file.name);
    file
      .text()
      .then(setCsv)
      .catch(() => setProblem('That file could not be read.'));
  }

  const preview = useMutation({
    mutationFn: () => api.previewRoster(orgId, csv),
    onSuccess: (p) => {
      setProblem(null);
      setConfirmingLarge(false);
      setPlan(p);
    },
    onError: (err: Error) => {
      setPlan(null);
      setProblem(err.message);
    },
  });

  const apply = useMutation({
    mutationFn: (confirmLargeRemoval: boolean) =>
      api.importRoster(orgId, csv, plan!.digest, confirmLargeRemoval),
    onSuccess: (r) => {
      setDone(`${r?.added ?? 0} added, ${r?.changed ?? 0} changed, ${r?.removed ?? 0} taken off.`);
      setPlan(null);
      setConfirmingLarge(false);
      void client.invalidateQueries({ queryKey: ['roster', orgId] });
    },
    onError: (err: unknown) => {
      // Defence in depth: if the server refuses because the plan removes more
      // than half the roster and the client did not catch it, open the same
      // confirmation dialog rather than showing a raw refusal.
      if (looksLikeLargeRemovalRefusal(err) && !confirmingLarge) {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setConfirmingLarge(true);
        return;
      }
      setProblem(api.errorMessage(err) ?? 'That import could not be applied.');
    },
  });

  // Would this plan take off more than half the active roster? The server flags
  // it, and the client recomputes so a missing field cannot slip a large
  // removal past the dialog. The denominator is the server's own `activeCount`
  // — the exact figure the >50% guard is enforced against — so the threshold the
  // sentence shows matches the one the server applies (Arch M3). It falls back
  // to summing the plan's active buckets only while running ahead of the API
  // that returns the field.
  const currentActive = plan
    ? (plan.activeCount ?? plan.remove.length + plan.changeRole.length + plan.unchanged)
    : 0;
  const isLarge =
    plan !== null &&
    plan.remove.length > 0 &&
    (plan.largeRemoval === true || plan.remove.length * 2 > currentActive);

  return (
    <Section title="Import people">
      <div className="max-w-[720px] space-y-4">
        <p className="text-[13px] text-[var(--ink-2)]">
          A CSV with an email column, and optionally a name and a role. This list becomes your
          people: anybody on it now and not in the file is taken off. That is the point — somebody
          who has left never answers, so the site never reads as clear and the number is wrong.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="cursor-pointer rounded-[var(--radius-2)] border border-[var(--line-2)] px-4 py-2 text-[13px] text-[var(--ink)] hover:bg-[var(--paper-3)]">
            Choose a file
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => take(e.target.files?.[0])}
            />
          </label>
          {fileName ? <span className="text-[13px] text-[var(--ink-2)]">{fileName}</span> : null}
        </div>

        {problem ? <Finding>{problem}</Finding> : null}
        {done ? <p className="text-[13px] text-[var(--ink-2)]">{done}</p> : null}

        {plan ? (
          <>
            <Metrics>
              <Metric label="To add" value={String(plan.add.length)} />
              <Metric label="Role changes" value={String(plan.changeRole.length)} />
              <Metric label="To take off" value={String(plan.remove.length)} />
              <Metric label="Unchanged" value={String(plan.unchanged)} />
            </Metrics>

            {plan.remove.length > 0 ? (
              <>
                <Finding tone={isLarge ? 'alert' : 'attention'}>
                  {isLarge
                    ? `This file removes ${plan.remove.length} of ${currentActive} people — more than half your people. That is usually a truncated export, not a real change. Check the list before applying.`
                    : plan.remove.length === 1
                      ? 'One person will be removed and will no longer be alerted.'
                      : `${plan.remove.length} people will be removed and will no longer be alerted.`}
                </Finding>
                <Table head={['Email', 'Access']}>
                  {plan.remove.slice(0, 50).map((person) => (
                    <Row key={person.email}>
                      <Cell>{person.email}</Cell>
                      <Cell>{roleLabel(person.role as api.Role)}</Cell>
                    </Row>
                  ))}
                </Table>
                {plan.remove.length > 50 ? (
                  <p className="text-[13px] text-[var(--ink-3)]">
                    and {plan.remove.length - 50} more
                  </p>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            disabled={!csv || preview.isPending}
            onClick={() => preview.mutate()}
          >
            Check the file
          </Button>
          <Button
            variant="primary"
            disabled={!plan || apply.isPending}
            onClick={() => (isLarge ? openLargeConfirm() : apply.mutate(false))}
          >
            {apply.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </div>

        {/*
          The confirmation the >50% guard exists for. It is a real dialog
          (`role="alertdialog"`), not a checkbox tucked beside Apply: removing
          more than half a building's people is the kind of action that is only
          ever right on purpose. Focus moves into it on open and returns to the
          Apply button on cancel; Escape cancels.
        */}
        {confirmingLarge && plan ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
              ref={dialogRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="large-removal-title"
              aria-describedby="large-removal-desc"
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelLargeConfirm();
              }}
              className="max-w-[520px] rounded-[var(--radius-2)] border border-[var(--line-strong)] bg-[var(--paper)] p-5 outline-none"
            >
              <h2
                id="large-removal-title"
                className="mb-2 text-[16px] font-[650] text-[var(--ink)]"
              >
                Remove more than half the roster?
              </h2>
              <p id="large-removal-desc" className="mb-4 max-w-prose text-[14px] text-[var(--ink)]">
                This will take <strong className="tabular">{plan.remove.length}</strong> people off
                the roster — more than half of the {currentActive} on it now. They will no longer be
                alerted. Do this only if the file really is the whole roster.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="primary"
                  danger
                  disabled={apply.isPending}
                  onClick={() => apply.mutate(true)}
                >
                  {apply.isPending ? 'Applying…' : `Yes, remove ${plan.remove.length} people`}
                </Button>
                <Button variant="quiet" onClick={cancelLargeConfirm}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/**
 * Whether an apply error is the server's >50%-removal refusal. The exact shape
 * the API returns is the setup-wizard-api branch's to settle, so this is
 * generous: a 409, or a detail flag, or the word "confirm" in the message.
 */
function looksLikeLargeRemovalRefusal(err: unknown): boolean {
  if (!(err instanceof api.ApiError)) return false;
  if (err.status === 409) return true;
  const flag = err.detail?.largeRemoval ?? err.detail?.requiresConfirmation;
  if (flag === true) return true;
  return /confirm|more than half|large removal/i.test(err.message);
}
